import { useMemo, useState } from "react";
import { Layout } from "@/components/layout";
import {
  useListSales,
  useGetSalesSyncStatus,
  useSyncSales,
  getListSalesQueryKey,
  getGetSalesSyncStatusQueryKey,
  type SaleRow,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2,
  AlertCircle,
  ExternalLink,
  Mail,
  RefreshCw,
} from "lucide-react";
import { Link } from "wouter";
import { formatMoney } from "@/components/inventory-cells";
import { PushToReachButton } from "@/components/push-to-reach";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function fmtDateTime(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtEventDate(s: string | null | undefined): string {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

// Compact "x minutes ago" style relative label for the last-synced timestamp.
function relativeTime(s: string | null | undefined): string {
  if (!s) return "never";
  const then = new Date(s).getTime();
  if (Number.isNaN(then)) return "never";
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
}

function reachListingHref(reachListingId?: string | null): string | null {
  if (!reachListingId) return null;
  return `https://tex.reachpro.com/inventory?uniqueInventoryId=${encodeURIComponent(
    reachListingId,
  )}`;
}

function ListingCell({ row }: { row: SaleRow }) {
  const identity = [row.section && `Sec ${row.section}`, row.row && `Row ${row.row}`]
    .filter(Boolean)
    .join(" · ");
  const href = reachListingHref(row.reachListingId);
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="font-medium text-foreground truncate">
          {row.eventName ?? "Unknown event"}
        </span>
        {row.listingNumber && (
          <span className="font-mono text-[10px] text-emerald-300/80 shrink-0">
            {row.listingNumber}
          </span>
        )}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label="Open listing in Reach"
            title="Open listing in Reach"
          >
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
      <div className="text-[11px] text-muted-foreground truncate">
        {[fmtEventDate(row.eventDate), row.venue, identity]
          .filter((p) => p && p !== "—")
          .join(" · ") || "—"}
      </div>
      <div className="text-[10px] text-muted-foreground/70 truncate">
        {fmtDateTime(row.saleDate)}
        {row.vendorAccountEmail ? ` · ${row.vendorAccountEmail}` : ""}
      </div>
    </div>
  );
}

function ReachCell({
  row,
  pushConfigured,
}: {
  row: SaleRow;
  pushConfigured: boolean;
}) {
  if (row.reachInvoiceId) {
    const inner = (
      <span className="inline-flex items-center gap-1 font-mono text-[11px] text-emerald-400">
        {row.reachInvoiceId}
        <ExternalLink className="h-3 w-3" />
      </span>
    );
    return row.reachInvoiceUrl ? (
      <a
        href={row.reachInvoiceUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:underline"
        title="Open sale in Reach"
      >
        {inner}
      </a>
    ) : (
      inner
    );
  }
  return (
    <PushToReachButton
      saleEmailId={row.saleEmailId}
      pushConfigured={pushConfigured}
    />
  );
}

function SaleTableRow({
  row,
  pushConfigured,
}: {
  row: SaleRow;
  pushConfigured: boolean;
}) {
  return (
    <tr className="border-b border-border/40 hover:bg-muted/40">
      <td className="px-3 py-1.5 max-w-[200px] sm:max-w-[360px]">
        <ListingCell row={row} />
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
        {row.listingQty ?? "—"}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap font-medium">
        {row.soldQty ?? "—"}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
        {row.remaining ?? "—"}
      </td>
      <td className="px-3 py-1.5 text-center whitespace-nowrap">
        {row.deliveryEmailId != null ? (
          <Link href={`/emails?focus=${row.deliveryEmailId}`}>
            <span
              className="inline-flex text-muted-foreground hover:text-foreground cursor-pointer"
              title="View delivery / payment email"
            >
              <Mail className="h-3.5 w-3.5" />
            </span>
          </Link>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
      <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap font-medium">
        {formatMoney(row.payout, row.currency)}
      </td>
      <td className="px-3 py-1.5 text-right whitespace-nowrap">
        <ReachCell row={row} pushConfigured={pushConfigured} />
      </td>
    </tr>
  );
}

export default function Sales() {
  const qc = useQueryClient();
  const [eventDate, setEventDate] = useState<string>("all");

  const { data, isLoading, isFetching } = useListSales();
  const { data: syncStatus } = useGetSalesSyncStatus();

  const sync = useSyncSales({
    mutation: {
      onSuccess: (summary) => {
        toast.success(
          `Sync complete — ${summary.matched ?? 0} matched, ${
            summary.inserted ?? 0
          } new.`,
        );
        qc.invalidateQueries({ queryKey: getListSalesQueryKey() });
        qc.invalidateQueries({ queryKey: getGetSalesSyncStatusQueryKey() });
      },
      onError: () => toast.error("Sales sync failed."),
    },
  });

  const rows = data?.rows ?? [];
  const unmatched = data?.unmatched ?? [];
  const pushConfigured = data?.pushConfigured ?? false;

  // Distinct event dates across the matched rows, for the filter dropdown.
  const eventDates = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) if (r.eventDate) set.add(r.eventDate);
    return Array.from(set).sort();
  }, [rows]);

  const visibleRows = useMemo(
    () =>
      eventDate === "all"
        ? rows
        : rows.filter((r) => r.eventDate === eventDate),
    [rows, eventDate],
  );

  return (
    <Layout>
      <div className="flex flex-col h-full w-full bg-background p-2 sm:p-4 overflow-hidden">
        <div className="mb-4 flex items-center justify-between gap-4 shrink-0 flex-wrap">
          <div>
            <h1 className="text-lg font-medium text-foreground">Sales</h1>
            <p className="text-xs text-muted-foreground">
              One row per confirmed sale, oldest first
              {data ? ` · ${data.total} sales` : ""}
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Select value={eventDate} onValueChange={setEventDate}>
              <SelectTrigger className="h-8 w-44 text-xs">
                <SelectValue placeholder="All event dates" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All event dates</SelectItem>
                {eventDates.map((d) => (
                  <SelectItem key={d} value={d}>
                    {fmtEventDate(d)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="text-right">
              <div className="text-[11px] text-muted-foreground">
                Last synced{" "}
                <span className="text-foreground">
                  {relativeTime(syncStatus?.lastSyncAt)}
                </span>
              </div>
              {syncStatus?.nextSyncAt && (
                <div className="text-[10px] text-muted-foreground/70">
                  Next ~{fmtDateTime(syncStatus.nextSyncAt)}
                </div>
              )}
            </div>

            <Button
              size="sm"
              variant="outline"
              className="rounded-full"
              onClick={() => sync.mutate({ data: {} })}
              disabled={sync.isPending}
            >
              {sync.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              ) : (
                <RefreshCw className="h-3.5 w-3.5 mr-1 text-blue-400" />
              )}
              Sync now
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto custom-scrollbar space-y-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="h-5 w-5 animate-spin text-primary" />
            </div>
          ) : rows.length === 0 && unmatched.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
              No confirmed sales yet.
            </div>
          ) : (
            <>
              <div className="rounded-md border border-border bg-card overflow-x-auto custom-scrollbar">
                <table className="w-full text-xs min-w-[640px]">
                  <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                    <tr className="text-left">
                      <th className="px-3 py-1.5 font-medium">Listing</th>
                      <th className="px-3 py-1.5 font-medium text-right">
                        Listed
                      </th>
                      <th className="px-3 py-1.5 font-medium text-right">Sold</th>
                      <th className="px-3 py-1.5 font-medium text-right">
                        Remaining
                      </th>
                      <th className="px-3 py-1.5 font-medium text-center">
                        Delivery
                      </th>
                      <th className="px-3 py-1.5 font-medium text-right">
                        Payment
                      </th>
                      <th className="px-3 py-1.5 font-medium text-right">Reach</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.length === 0 ? (
                      <tr>
                        <td
                          colSpan={7}
                          className="px-3 py-6 text-center text-muted-foreground"
                        >
                          No sales for this event date.
                        </td>
                      </tr>
                    ) : (
                      visibleRows.map((row) => (
                        <SaleTableRow
                          key={row.saleEmailId}
                          row={row}
                          pushConfigured={pushConfigured}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {unmatched.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-medium text-amber-400 uppercase tracking-wide">
                    <AlertCircle className="h-3.5 w-3.5" />
                    Unmatched sales ({unmatched.length})
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    Sale emails that did not resolve to a listing. Match or ignore
                    them from the Emails page.
                  </p>
                  <div className="rounded-md border border-border bg-card overflow-x-auto custom-scrollbar">
                    <table className="w-full text-xs min-w-[640px]">
                      <thead className="bg-muted/40 text-muted-foreground">
                        <tr className="text-left">
                          <th className="px-3 py-1.5 font-medium">Sale</th>
                          <th className="px-3 py-1.5 font-medium text-right">
                            Sold
                          </th>
                          <th className="px-3 py-1.5 font-medium text-right">
                            Payment
                          </th>
                          <th className="px-3 py-1.5 font-medium text-right">
                            Reach
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmatched.map((row) => (
                          <tr
                            key={row.saleEmailId}
                            className="border-b border-border/40 hover:bg-muted/40"
                          >
                            <td className="px-3 py-1.5 max-w-[420px]">
                              <Link href={`/emails?focus=${row.saleEmailId}`}>
                                <span className="text-primary hover:underline cursor-pointer truncate block">
                                  {row.emailSubject ??
                                    row.eventName ??
                                    "View email"}
                                </span>
                              </Link>
                              <div className="text-[10px] text-muted-foreground/70 truncate">
                                {fmtDateTime(row.saleDate)}
                                {row.vendorAccountEmail
                                  ? ` · ${row.vendorAccountEmail}`
                                  : ""}
                              </div>
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
                              {row.soldQty ?? "—"}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">
                              {formatMoney(row.payout, row.currency)}
                            </td>
                            <td className="px-3 py-1.5 text-right whitespace-nowrap">
                              <ReachCell
                                row={row}
                                pushConfigured={pushConfigured}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
          {isFetching && !isLoading && (
            <div className="py-2 text-center">
              <Loader2 className="h-4 w-4 animate-spin text-primary inline" />
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
